/**
 * Re-send planning — which selected killed drops go, when, and onto which day.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Mirrors the endpoint's own rules (shared/smswiser/resend), so what the dialog
 * shows is what the backend will do.
 */

import {
  nextOriginalResendTime,
  resendScheduleIssue,
  resendWorkDate,
  type ResendScheduleIssue,
} from '@trafficpulse/shared';

import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';

/** now = as soon as possible · original = each drop's own planned clock time,
 *  next time it comes round · pick = one start for all. */
export type ResendMode = 'now' | 'original' | 'pick';

export interface ResendPlanRow {
  drop: PreparedDrop;
  /** Why this drop is not re-sent, or null when it is. */
  blocked: string | null;
  /** null = now. */
  sendAt: Date | null;
  workDate: string;
}

export interface ResendPlan {
  ready: ResendPlanRow[];
  blocked: ResendPlanRow[];
  /** Set in pick mode when the chosen start cannot be used. */
  issue: ResendScheduleIssue | 'missing' | null;
}

export const isKilled = (d: PreparedDrop) => d.status === 'KILLED';

/** A killed drop with no re-send yet — the only kind the actions count. */
export const isResendable = (d: PreparedDrop) => isKilled(d) && !d.resentAs;

export function resentLabel(link: PreparedDrop['resentAs']): string {
  if (!link) return '';
  if (link.sentDropId != null) return `Re-sent as #${link.sentDropId}`;
  return link.status === 'DRAFT' ? 'Re-send waiting in Drafts' : 'Re-send in progress';
}

/** When the killed drop was meant to start — its schedule, else when it went. */
export function plannedStartOf(d: PreparedDrop): Date | null {
  const iso = d.executeAfter ?? d.sentAt;
  if (!iso) return null;
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? null : at;
}

function sendAtFor(d: PreparedDrop, mode: ResendMode, picked: Date | null, now: Date) {
  if (mode === 'now') return null;
  if (mode === 'pick') return picked;
  const planned = plannedStartOf(d);
  return planned ? nextOriginalResendTime(planned, now) : null;
}

function pickIssue(mode: ResendMode, picked: Date | null, now: Date): ResendPlan['issue'] {
  if (mode !== 'pick') return null;
  if (!picked) return 'missing';
  return resendScheduleIssue(picked, now);
}

export function planResend(
  drops: PreparedDrop[],
  mode: ResendMode,
  picked: Date | null,
  now: Date = new Date(),
): ResendPlan {
  const ready: ResendPlanRow[] = [];
  const blocked: ResendPlanRow[] = [];
  for (const drop of drops.filter(isKilled)) {
    const sendAt = sendAtFor(drop, mode, picked, now);
    const workDate = resendWorkDate({
      sourceWorkDateYmd: drop.workDate,
      plannedAt: plannedStartOf(drop),
      sendAt: sendAt ?? now,
    });
    const row = { drop, sendAt, workDate, blocked: drop.resentAs ? resentLabel(drop.resentAs) : null };
    (row.blocked ? blocked : ready).push(row);
  }
  return { ready, blocked, issue: pickIssue(mode, picked, now) };
}

/** One request per campaign — the endpoint is campaign-scoped. */
export function groupByCampaign(rows: ResendPlanRow[]) {
  const groups = new Map<string, { countryKey: string; wiserId: number; rows: ResendPlanRow[] }>();
  for (const row of rows) {
    const key = `${row.drop.countryKey}|${row.drop.wiserId}`;
    const group = groups.get(key) ?? { countryKey: row.drop.countryKey, wiserId: row.drop.wiserId, rows: [] };
    group.rows.push(row);
    groups.set(key, group);
  }
  return [...groups.values()];
}
